import { httpsCallable } from 'firebase/functions'
import { signInWithCustomToken } from 'firebase/auth'
import { collection, doc, getDocs, onSnapshot, query, where, type Unsubscribe } from 'firebase/firestore'
import { firebaseAuth, firebaseFunctions, firestoreDb } from './firebase'
import type { ChecklistWorkflowStatus } from '../types/schema'
import type { FieldPassAssignment, FieldPassRecord, FieldPassSessionSnapshot } from '../types/fieldPass'

const createFieldPassCallable = httpsCallable<
  {
    workspaceId: string
    targetIds: string[]
    expiresInHours?: number
    deleteOnCompletion?: boolean
    targetEmail?: string | null
    deliveryPhone?: string | null
  },
  { pass: FieldPassRecord; url: string; token: string }
>(firebaseFunctions, 'createFieldPass')

const listFieldPassesCallable = httpsCallable<
  { workspaceId: string },
  { passes: FieldPassRecord[] }
>(firebaseFunctions, 'listFieldPasses')

const listFieldPassAssignmentsCallable = httpsCallable<
  { workspaceId: string; checklistItemIds: string[] },
  { assignments: FieldPassAssignment[] }
>(firebaseFunctions, 'listFieldPassAssignments')

const revokeFieldPassCallable = httpsCallable<
  { passId: string },
  { passId: string; status: 'REVOKED' }
>(firebaseFunctions, 'revokeFieldPass')

const addFieldPassTargetsCallable = httpsCallable<
  { passId: string; targetIds: string[] },
  { pass: FieldPassRecord }
>(firebaseFunctions, 'addFieldPassTargets')

const removeFieldPassTargetCallable = httpsCallable<
  { passId: string; targetId: string },
  { pass: FieldPassRecord }
>(firebaseFunctions, 'removeFieldPassTarget')

const redeemFieldPassCallable = httpsCallable<
  { token: string; displayName: string; fullName?: string | null; phone?: string | null },
  { customToken: string; passId: string; sessionId: string; workspaceId: string }
>(firebaseFunctions, 'redeemFieldPass')

const getFieldPassSessionCallable = httpsCallable<
  { passId: string },
  FieldPassSessionSnapshot
>(firebaseFunctions, 'getFieldPassSession')

const sendFieldPassMessageCallable = httpsCallable<
  { passId: string; text: string },
  FieldPassSessionSnapshot
>(firebaseFunctions, 'sendFieldPassMessage')

const updateFieldPassTaskStatusCallable = httpsCallable<
  { passId: string; taskId: string; workflowStatus: ChecklistWorkflowStatus },
  FieldPassSessionSnapshot
>(firebaseFunctions, 'updateFieldPassTaskStatus')

const addFieldPassTaskCommentCallable = httpsCallable<
  { passId: string; taskId: string; comment: string },
  FieldPassSessionSnapshot
>(firebaseFunctions, 'addFieldPassTaskComment')

const recordFieldPassScanCallable = httpsCallable<
  { passId: string; taskId?: string | null; barcode: string; mode: 'IN' | 'OUT' },
  FieldPassSessionSnapshot
>(firebaseFunctions, 'recordFieldPassScan')

const addFieldPassChatMemberCallable = httpsCallable<
  { passId: string; memberUid: string },
  { passId: string; channelId: string }
>(firebaseFunctions, 'addFieldPassChatMember')

const uploadFieldPassProofCallable = httpsCallable<
  { passId: string; taskId: string; fileName: string; contentType: string; base64: string },
  FieldPassSessionSnapshot
>(firebaseFunctions, 'uploadFieldPassProof')

const closeAndCleanUpFieldPassCallable = httpsCallable<
  { passId: string },
  { passId: string; status: 'REVOKED'; cleanedUpAt: string; summary: Record<string, unknown> | null }
>(firebaseFunctions, 'closeAndCleanUpFieldPass')

export const closeAndCleanUpFieldPass = async (passId: string) => {
  return (await closeAndCleanUpFieldPassCallable({ passId })).data
}

export const buildFieldPassUrl = (token: string): string => {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://timelining.app'
  return `${origin}/field-pass/${token}`
}

export const createFieldPass = async (input: {
  workspaceId: string
  targetIds: string[]
  expiresInHours?: number
  deleteOnCompletion?: boolean
  /** Optional recipient email. Empty string is coerced to null (anonymous pass). */
  targetEmail?: string | null
  /** Optional recipient phone for WhatsApp delivery (E.164). */
  deliveryPhone?: string | null
}) => (await createFieldPassCallable({
  ...input,
  targetEmail: input.targetEmail?.trim() || null,
  deliveryPhone: input.deliveryPhone?.trim() || null,
})).data

export const subscribeFieldPassDelegationGrant = (
  workspaceId: string,
  uid: string,
  onChange: (canDelegate: boolean) => void,
): Unsubscribe => {
  if (!workspaceId || !uid) {
    onChange(false)
    return () => undefined
  }
  return onSnapshot(
    doc(firestoreDb, 'users', uid, 'workspaceMemberships', workspaceId),
    (snapshot) => onChange(snapshot.exists() && snapshot.data().canDelegateFieldPass === true),
    () => onChange(false),
  )
}

export const listFieldPasses = async (workspaceId: string) =>
  (await listFieldPassesCallable({ workspaceId })).data.passes

export const listFieldPassesFromFirestore = async (workspaceId: string): Promise<FieldPassRecord[]> => {
  const snapshot = await getDocs(query(collection(firestoreDb, 'field_passes'), where('workspaceId', '==', workspaceId)))
  return snapshot.docs
    .map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }) as FieldPassRecord)
    .sort((left, right) => {
      const leftMs = left.createdAt?.toMillis?.() ?? 0
      const rightMs = right.createdAt?.toMillis?.() ?? 0
      return rightMs - leftMs
    })
}

export const listFieldPassAssignments = async (workspaceId: string, checklistItemIds: string[]) =>
  (await listFieldPassAssignmentsCallable({ workspaceId, checklistItemIds })).data.assignments

export const listFieldPassAssignmentsFromFirestore = async (
  workspaceId: string,
  checklistItemIds: string[],
): Promise<FieldPassAssignment[]> => {
  const uniqueIds = Array.from(new Set(checklistItemIds.map((id) => id.trim()).filter(Boolean))).slice(0, 30)
  if (uniqueIds.length === 0) return []
  const chunks: string[][] = []
  for (let index = 0; index < uniqueIds.length; index += 10) chunks.push(uniqueIds.slice(index, index + 10))
  const snapshots = await Promise.all(chunks.map((chunk) => getDocs(query(
    collection(firestoreDb, 'field_pass_assignments'),
    where('workspaceId', '==', workspaceId),
    where('checklistItemId', 'in', chunk),
  ))))
  return snapshots
    .flatMap((snapshot) => snapshot.docs)
    .map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }) as FieldPassAssignment)
}

export const revokeFieldPass = async (passId: string) =>
  (await revokeFieldPassCallable({ passId })).data

export const addFieldPassTargets = async (passId: string, targetIds: string[]) =>
  (await addFieldPassTargetsCallable({ passId, targetIds })).data.pass

export const removeFieldPassTarget = async (passId: string, targetId: string) =>
  (await removeFieldPassTargetCallable({ passId, targetId })).data.pass

export const redeemFieldPass = async (input: {
  token: string
  displayName: string
  fullName?: string | null
  phone?: string | null
}) => {
  const result = await redeemFieldPassCallable(input)
  await signInWithCustomToken(firebaseAuth, result.data.customToken)
  return result.data
}

export const getFieldPassSession = async (passId: string) =>
  (await getFieldPassSessionCallable({ passId })).data

export const sendFieldPassMessage = async (passId: string, text: string) =>
  (await sendFieldPassMessageCallable({ passId, text })).data

export const updateFieldPassTaskStatus = async (
  passId: string,
  taskId: string,
  workflowStatus: ChecklistWorkflowStatus,
) => (await updateFieldPassTaskStatusCallable({ passId, taskId, workflowStatus })).data

export const addFieldPassTaskComment = async (passId: string, taskId: string, comment: string) =>
  (await addFieldPassTaskCommentCallable({ passId, taskId, comment })).data

export const recordFieldPassScan = async (
  passId: string,
  taskId: string | null,
  barcode: string,
  mode: 'IN' | 'OUT',
) => (await recordFieldPassScanCallable({ passId, taskId, barcode, mode })).data

export const addFieldPassChatMember = async (passId: string, memberUid: string) =>
  (await addFieldPassChatMemberCallable({ passId, memberUid })).data

export const uploadFieldPassProof = async (passId: string, taskId: string, file: File) => {
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Could not read proof file.'))
    reader.onload = () => {
      const value = typeof reader.result === 'string' ? reader.result : ''
      resolve(value.includes(',') ? value.split(',').pop() ?? '' : value)
    }
    reader.readAsDataURL(file)
  })
  return (await uploadFieldPassProofCallable({
    passId,
    taskId,
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    base64,
  })).data
}

/**
 * Web inbox read: Field Passes addressed to this user by email, still active and unexpired.
 * Never returns anonymous (unaddressed) passes.
 */
export const listFieldPassesForEmail = async (
  email: string,
  db: typeof firestoreDb = firestoreDb,
): Promise<FieldPassRecord[]> => {
  const normalized = email.trim().toLowerCase()
  if (!normalized) return []
  const q = query(
    collection(db, 'field_passes'),
    where('targetEmailNormalized', '==', normalized),
    where('status', '==', 'ACTIVE'),
  )
  const snap = await getDocs(q)
  const now = Date.now()
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as FieldPassRecord)
    .filter((fp) => !fp.expiresAt || ((fp.expiresAt as { toMillis?: () => number }).toMillis?.() ?? 0) > now)
}
